import { Calendar, MapPin, Users, ShoppingCart } from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import SeatMap from "../../../components/ground/seat-map/SeatMap";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import type { RootState } from "../../../store";
import {
  useGetEventByIdQuery,
  useGetEventSeatAvailabilityQuery,
} from "../../../store/api/eventApi";
import {
  setSelectedSeats,
  addSelectedSeat,
  removeSelectedSeat,
} from "../../../store/slices/bookingSlice";
import { ROUTES } from "../../../utils/constants";
import { formatCurrency, formatDateTime } from "../../../utils/helpers";

const SeatSelection: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedSeats } = useSelector((state: RootState) => state.booking);
  const [selectedTicketType, setSelectedTicketType] = useState<string>("");

  const { data: eventData, isLoading: eventLoading } = useGetEventByIdQuery(eventId!);
  const { data: seatData, isLoading: seatLoading } = useGetEventSeatAvailabilityQuery(eventId!);

  const event = eventData?.data;
  const seatAvailability = seatData?.data;

  useEffect(() => {
    // Clear selected seats when component mounts
    dispatch(setSelectedSeats([]));
  }, [dispatch]);

  const handleSeatClick = (seatId: string) => {
    if (selectedSeats.includes(seatId)) {
      dispatch(removeSelectedSeat(seatId));
    } else {
      dispatch(addSelectedSeat(seatId));
    }
  };

  const handleProceedToCheckout = () => {
    if (selectedSeats.length === 0) return;
    navigate(`${ROUTES.CHECKOUT}?eventId=${eventId}`);
  };

  const getTotalAmount = () => {
    if (!event || !selectedTicketType) return 0;
    const ticketType = event.ticketTypes.find(t => t.id === selectedTicketType);
    return (ticketType?.price || 0) * selectedSeats.length;
  };

  if (eventLoading || seatLoading) {
    return (
      <div className='container py-8'>
        <div className='text-center'>
          <div className='mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
          <p>Loading seat availability...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className='container py-8'>
        <div className='text-center'>
          <h1 className='mb-4 text-2xl font-bold'>Event Not Found</h1>
          <Button onClick={() => navigate(ROUTES.EVENTS)}>Back to Events</Button>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mx-auto max-w-6xl'>
        <div className='mb-8'>
          <h1 className='mb-4 text-3xl font-bold'>Select Your Seats</h1>
          <div className='grid grid-cols-1 gap-4 text-sm md:grid-cols-3'>
            <div className='flex items-center gap-2'>
              <Calendar className='h-4 w-4 text-muted-foreground' />
              <span>{formatDateTime(event.datetime)}</span>
            </div>
            <div className='flex items-center gap-2'>
              <MapPin className='h-4 w-4 text-muted-foreground' />
              <span>{event.ground?.location.city}</span>
            </div>
            <div className='flex items-center gap-2'>
              <Users className='h-4 w-4 text-muted-foreground' />
              <span>{event.ground?.capacity.toLocaleString()} capacity</span>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-8 lg:grid-cols-4'>
          {/* Seat Map */}
          <div className='lg:col-span-3'>
            <Card>
              <CardHeader>
                <CardTitle>Seating Chart</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='mb-6'>
                  {/* Seat Legend */}
                  <div className='flex flex-wrap gap-4 text-sm'>
                    <div className='flex items-center gap-2'>
                      <div className='h-4 w-4 rounded border border-green-300 bg-green-200'></div>
                      <span>Available</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='h-4 w-4 rounded border border-blue-600 bg-blue-500'></div>
                      <span>Selected</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='h-4 w-4 rounded border border-red-300 bg-red-200'></div>
                      <span>Unavailable</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='h-4 w-4 rounded border border-yellow-300 bg-yellow-200'></div>
                      <span>Reserved</span>
                    </div>
                  </div>
                </div>

                <SeatMap
                  ground={event.ground!}
                  selectedSeats={selectedSeats}
                  unavailableSeats={
                    seatAvailability?.seats?.filter(s => s.status !== "available").map(s => s.id) ||
                    []
                  }
                  onSeatClick={handleSeatClick}
                />
              </CardContent>
            </Card>
          </div>

          {/* Booking Summary */}
          <div className='space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle>Event Details</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <h3 className='font-semibold'>{event.title}</h3>
                  <p className='line-clamp-2 text-sm text-muted-foreground'>{event.description}</p>
                </div>
                <div className='text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground'>Date & Time</span>
                    <span>{formatDateTime(event.datetime)}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground'>Venue</span>
                    <span>{event.ground?.name}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ticket Types</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                {event.ticketTypes.map(ticketType => (
                  <div
                    key={ticketType.id}
                    className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                      selectedTicketType === ticketType.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => setSelectedTicketType(ticketType.id)}
                  >
                    <div className='flex items-center justify-between'>
                      <div>
                        <h4 className='font-medium'>{ticketType.name}</h4>
                        <p className='text-xs text-muted-foreground'>
                          {ticketType.available} of {ticketType.quantity} available
                        </p>
                      </div>
                      <div className='text-right'>
                        <div className='font-semibold'>{formatCurrency(ticketType.price)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <ShoppingCart className='h-5 w-5' />
                  Your Selection
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                {selectedSeats.length > 0 ? (
                  <>
                    <div>
                      <h4 className='mb-2 font-medium'>Selected Seats</h4>
                      <div className='grid grid-cols-3 gap-2'>
                        {selectedSeats.map(seatId => (
                          <Badge key={seatId} variant='secondary' className='justify-center'>
                            {seatId.split("-").slice(-2).join("")}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className='space-y-2 text-sm'>
                      <div className='flex justify-between'>
                        <span>Seats ({selectedSeats.length})</span>
                        <span>{formatCurrency(getTotalAmount())}</span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Service Fee</span>
                        <span>{formatCurrency(getTotalAmount() * 0.05)}</span>
                      </div>
                      <div className='flex justify-between border-t pt-2 font-semibold'>
                        <span>Total</span>
                        <span>{formatCurrency(getTotalAmount() + getTotalAmount() * 0.05)}</span>
                      </div>
                    </div>

                    <Button
                      className='w-full'
                      onClick={handleProceedToCheckout}
                      disabled={!selectedTicketType || selectedSeats.length === 0}
                    >
                      Proceed to Checkout
                    </Button>
                  </>
                ) : (
                  <div className='py-8 text-center text-muted-foreground'>
                    <p>Select seats to continue</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeatSelection;
